// =============================================================================
// inspectionPdf.ts — T-155
//
// Professional A4 inspection report. Coretel-branded header band, item
// identity panel, prominent result badge, structured visual checks block,
// notes, defects callout, photo embed, footer with metadata.
//
// Re-uses the lazy pdfmake loader pattern from features/quotes/quotePdf.ts
// and pulls the same logo from /coretel-logo-full.png in public/.
//
// 12 Oct 2026 — getPdfMake() and fetchLogoDataUrl() are exported so the
// register export (inspectionRegisterExport.ts) shares one loader and one logo.
// =============================================================================

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type defs
import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

const COLOR = {
  primary:     "#ff7844",
  primaryWash: "#fff4ee",
  primarySoft: "#ffd9c7",
  ink:         "#111111",
  text:        "#1a1a1a",
  muted:       "#666666",
  dim:         "#999999",
  rule:        "#e5e5e5",
  ruleDark:    "#cccccc",
  rowAlt:      "#fafafa",
  ok:          "#15803d",
  okWash:      "#dcfce7",
  warn:        "#b45309",
  warnWash:    "#fef3c7",
  danger:      "#b91c1c",
  dangerWash:  "#fee2e2",
};

// -----------------------------------------------------------------------------
// pdfmake bootstrap
// -----------------------------------------------------------------------------

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-pdfmake-tag="${tag}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load",  () => resolve());
      existing.addEventListener("error", () => reject(new Error(`Failed to (re)load ${tag}`)));
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.pdfmakeTag = tag;
    script.onload  = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error(`Failed to load ${tag} from ${src}`));
    document.head.appendChild(script);
  });
}

export async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl,  "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  return (window as any).pdfMake;
}

export async function fetchLogoDataUrl(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Public types
// -----------------------------------------------------------------------------

export interface RiggingPdfArgs {
  kind: "rigging";
  item: {
    code:              string;
    asset_tag?:        string;
    display_name?:     string | null;
    item_class?:       string | null;
    item_type?:        string | null;
    make?:             string | null;
    model?:            string | null;
    serial_number?:    string | null;
    swl_kg?:           number | null;
    rated_load_kn?:    number | null;
    manufacture_date?: string | null;
    in_service_date?:  string | null;
    destroy_by_date?:  string | null;
    kit_name?:         string | null;
    assigned_to_name?: string | null;
  };
  inspection: {
    inspection_date:     string;
    inspection_type?:    string | null;
    result:              string;
    tag_colour?:         string | null;
    tag_number?:         string | null;
    next_inspection_due?: string | null;
    findings?:           string | null;
    condition_notes?:    string | null;
    defects_found:       boolean;
    defect_severity?:    string | null;
    defect_summary?:     string | null;
    action_required?:    string | null;
  };
  inspectorName: string;
  photoDataUrl?: string;
}

export interface ToolPdfArgs {
  kind: "power_tool";
  item: {
    code:              string;
    asset_tag?:        string;
    display_name?:     string | null;
    item_class?:       string | null;
    make?:             string | null;
    model?:            string | null;
    serial_number?:    string | null;
    kit_name?:         string | null;
    assigned_to_name?: string | null;
  };
  test: {
    tested_at:                string;
    result:                   string;
    tag_colour?:              string | null;
    test_tag_number?:         string | null;
    visual_inspection_result?: string | null;
    next_test_due?:           string | null;
    notes?:                   string | null;
    defects_found:            boolean;
    failure_reason?:          string | null;
    action_required?:         string | null;
  };
  inspectorName: string;
  photoDataUrl?: string;
}

export type InspectionPdfArgs = RiggingPdfArgs | ToolPdfArgs;

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export async function downloadInspectionPdf(args: InspectionPdfArgs): Promise<void> {
  const [pdfMake, logoDataUrl] = await Promise.all([
    getPdfMake(),
    fetchLogoDataUrl("/coretel-logo-full.png"),
  ]);
  const docDefinition = buildDocDefinition(args, logoDataUrl);
  const pdfDocGen = pdfMake.createPdf(docDefinition);
  pdfDocGen.download(buildFilename(args));
}

function buildFilename(args: InspectionPdfArgs): string {
  const date = (args.kind === "rigging"
    ? args.inspection.inspection_date
    : (args.test.tested_at ?? "").slice(0, 10)
  ).replace(/-/g, "");
  const safeCode = (args.item.code ?? "ITEM").replace(/[^a-zA-Z0-9-]/g, "-");
  return `Inspection_${safeCode}_${date}.pdf`;
}

// -----------------------------------------------------------------------------
// Doc builder
// -----------------------------------------------------------------------------

function buildDocDefinition(args: InspectionPdfArgs, logoDataUrl: string | null): any {
  const isRig    = args.kind === "rigging";
  const result   = isRig ? args.inspection.result : (args.test.result ?? "—");
  const isFail   = ["fail", "quarantine"].includes(result.toLowerCase());
  const isWarn   = result.toLowerCase().includes("restrict");
  const resultColor    = isFail ? COLOR.danger : isWarn ? COLOR.warn : COLOR.ok;
  const resultWashColor = isFail ? COLOR.dangerWash : isWarn ? COLOR.warnWash : COLOR.okWash;

  const reportTitle = isRig
    ? "Rigging Inspection Report"
    : "Power Tool Test &amp; Tag Report";

  // Display title — fall back to code if display_name is absent
  const itemTitle = args.item.display_name?.trim() || args.item.code;

  // Identity rows for the panel
  const identityRows: { label: string; value: string }[] = [];
  identityRows.push({ label: "Asset code", value: args.item.code });
  if (args.item.asset_tag && args.item.asset_tag !== args.item.code) {
    identityRows.push({ label: "Asset tag", value: args.item.asset_tag });
  }
  if (args.item.serial_number) identityRows.push({ label: "Serial no.", value: args.item.serial_number });
  if (args.item.make || args.item.model) {
    identityRows.push({
      label: "Make / model",
      value: [args.item.make, args.item.model].filter(Boolean).join(" "),
    });
  }
  if (isRig && args.item.item_type)  identityRows.push({ label: "Type",  value: args.item.item_type! });
  if (args.item.item_class)          identityRows.push({ label: "Class", value: args.item.item_class! });
  if (args.item.kit_name)            identityRows.push({ label: "Kit",   value: args.item.kit_name! });
  if (isRig && args.item.swl_kg != null) identityRows.push({ label: "SWL",   value: `${args.item.swl_kg} kg` });
  if (isRig && args.item.rated_load_kn != null) identityRows.push({ label: "Rated", value: `${args.item.rated_load_kn} kN` });
  if (args.item.manufacture_date)    identityRows.push({ label: "Manufactured", value: args.item.manufacture_date });
  if (isRig && args.item.in_service_date) identityRows.push({ label: "In service since", value: args.item.in_service_date! });
  if (isRig && args.item.destroy_by_date) identityRows.push({ label: "Destroy by", value: args.item.destroy_by_date! });
  identityRows.push({ label: "Allocated to", value: args.item.assigned_to_name ?? "Unassigned" });

  // Inspection summary rows
  const inspectionRows: { label: string; value: string; emphasise?: boolean }[] = [];
  if (isRig) {
    const ins = args.inspection;
    inspectionRows.push({ label: "Inspection date", value: ins.inspection_date });
    inspectionRows.push({ label: "Inspector",       value: args.inspectorName });
    inspectionRows.push({ label: "Type",            value: ins.inspection_type ?? "Quarterly" });
    inspectionRows.push({ label: "New tag",         value: [ins.tag_colour, ins.tag_number].filter(Boolean).join(" · ") || "—" });
    if (ins.next_inspection_due) inspectionRows.push({ label: "Next due", value: ins.next_inspection_due });
  } else {
    const t = args.test;
    inspectionRows.push({ label: "Test date",       value: (t.tested_at ?? "").slice(0, 10) });
    inspectionRows.push({ label: "Tester",          value: args.inspectorName });
    inspectionRows.push({ label: "Visual",          value: t.visual_inspection_result ?? "—" });
    inspectionRows.push({ label: "Test tag",        value: [t.tag_colour, t.test_tag_number].filter(Boolean).join(" · ") || "—" });
    if (t.next_test_due) inspectionRows.push({ label: "Next due", value: t.next_test_due });
  }

  // Visual checks — parse out the structured "Visual checks:" block from
  // condition_notes (composed by the form). Falls back to plain text if
  // no structure detected.
  const conditionText = isRig ? (args.inspection.condition_notes ?? "") : (args.test.notes ?? "");
  const { checkLines, freeNotes } = splitConditionNotes(conditionText, args);

  const defectBlock = isRig && args.inspection.defects_found
    ? buildDefectBlock(
        args.inspection.defect_severity ?? "Defect",
        args.inspection.defect_summary  ?? "—",
        args.inspection.action_required ?? null,
      )
    : !isRig && args.test.defects_found
      ? buildDefectBlock(
          "Failure",
          args.test.failure_reason ?? "—",
          args.test.action_required ?? null,
        )
      : null;

  return {
    pageSize: "A4",
    pageMargins: [40, 130, 40, 50],   // top margin generous for header band
    info: {
      title:   `Inspection ${args.item.code}`,
      author:  "Coretel Ops",
      subject: reportTitle,
    },

    // -------------------------------------------------------------------
    // Header — branded orange band, logo on the left, title + result on right
    // -------------------------------------------------------------------
    header: function (currentPage: number) {
      if (currentPage !== 1) {
        // Subsequent pages get a slim continuation strip
        return {
          canvas: [
            { type: "rect", x: 0, y: 0, w: 595, h: 30, color: COLOR.primary },
          ],
          margin: [0, 0, 0, 0],
        };
      }
      return {
        stack: [
          // Coloured band background
          {
            canvas: [
              { type: "rect", x: 0,   y: 0,  w: 595, h: 100, color: COLOR.primary },
              { type: "rect", x: 0,   y: 100, w: 595, h: 4,   color: COLOR.ink },
            ],
            absolutePosition: { x: 0, y: 0 },
          },
          // Header content overlay
          {
            columns: [
              {
                width: 200,
                stack: logoDataUrl
                  ? [{ image: logoDataUrl, width: 130, margin: [0, 22, 0, 0] }]
                  : [{ text: "CORETEL OPS", style: "logoFallback", margin: [0, 32, 0, 0] }],
              },
              {
                width: "*",
                stack: [
                  { text: reportTitle.toUpperCase(), style: "headerTitle" },
                  { text: `Asset ${args.item.code}`, style: "headerSubtitle" },
                ],
                alignment: "right",
                margin: [0, 24, 40, 0],
              },
            ],
            absolutePosition: { x: 40, y: 0 },
          },
        ],
      };
    },

    // -------------------------------------------------------------------
    // Body
    // -------------------------------------------------------------------
    content: [
      // Asset name + result badge
      {
        columns: [
          {
            width: "*",
            stack: [
              { text: itemTitle, style: "h1" },
              {
                text: [
                  args.item.item_class ?? "",
                  args.item.kit_name ? ` · ${args.item.kit_name}` : "",
                ].join(""),
                style: "subhead",
                margin: [0, 2, 0, 0],
              },
            ],
          },
          {
            width: 130,
            stack: [
              {
                text: result.toUpperCase(),
                color: resultColor,
                bold: true,
                fontSize: 14,
                alignment: "center",
                margin: [0, 8, 0, 8],
                background: resultWashColor,
              },
            ],
            margin: [0, 4, 0, 0],
          },
        ],
        margin: [0, 0, 0, 14],
      },

      // -- Asset identity panel --
      sectionTitle("Asset identity"),
      identityTable(identityRows),

      // -- Inspection details panel --
      { text: "", margin: [0, 8, 0, 0] },
      sectionTitle("Inspection details"),
      inspectionTable(inspectionRows),

      // -- Visual checks --
      ...(checkLines.length > 0 ? [
        { text: "", margin: [0, 8, 0, 0] },
        sectionTitle("Visual checks"),
        checksTable(checkLines),
      ] : []),

      // -- Notes --
      ...(freeNotes.trim() ? [
        { text: "", margin: [0, 8, 0, 0] },
        sectionTitle("Notes"),
        { text: freeNotes.trim(), style: "para", margin: [0, 0, 0, 0] },
      ] : []),

      // -- Defect callout --
      ...(defectBlock ? [defectBlock] : []),

      // -- Photo --
      ...(args.photoDataUrl ? [
        { text: "", margin: [0, 12, 0, 0] },
        sectionTitle("Inspection photo"),
        {
          image: args.photoDataUrl,
          fit: [515, 360],
          alignment: "center",
          margin: [0, 4, 0, 0],
        },
      ] : []),
    ],

    // -------------------------------------------------------------------
    // Footer
    // -------------------------------------------------------------------
    footer: function (currentPage: number, pageCount: number) {
      return {
        margin: [40, 0, 40, 18],
        stack: [
          { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: COLOR.rule }] },
          {
            columns: [
              { text: `Coretel Operations Platform — generated ${formatNow()}`, style: "footer" },
              { text: `Page ${currentPage} of ${pageCount}`, style: "footer", alignment: "right" },
            ],
            margin: [0, 6, 0, 0],
          },
        ],
      };
    },

    // -------------------------------------------------------------------
    styles: {
      logoFallback:  { fontSize: 18, bold: true, color: "#ffffff" },
      headerTitle:   { fontSize: 14, bold: true, color: "#ffffff", characterSpacing: 1 },
      headerSubtitle:{ fontSize: 10, color: "#ffe7d8", margin: [0, 2, 0, 0] },
      h1:            { fontSize: 18, bold: true, color: COLOR.ink },
      subhead:       { fontSize: 10, color: COLOR.muted },
      sectionTitle:  { fontSize: 9, bold: true, color: COLOR.primary, characterSpacing: 1 },
      identLabel:    { fontSize: 8, color: COLOR.muted, characterSpacing: 0.5 },
      identValue:    { fontSize: 10, color: COLOR.text },
      para:          { fontSize: 10, color: COLOR.text, lineHeight: 1.4 },
      footer:        { fontSize: 8, color: COLOR.dim },
      checkLabel:    { fontSize: 10, color: COLOR.text },
      checkResultPass: { fontSize: 9, bold: true, color: COLOR.ok,    alignment: "center" },
      checkResultFail: { fontSize: 9, bold: true, color: COLOR.danger, alignment: "center" },
      checkResultNa:   { fontSize: 9, bold: true, color: COLOR.muted,  alignment: "center" },
    },
    defaultStyle: { font: "Roboto" },
  };
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function sectionTitle(label: string): any {
  return {
    stack: [
      { text: label.toUpperCase(), style: "sectionTitle" },
      { canvas: [{ type: "line", x1: 0, y1: 2, x2: 515, y2: 2, lineWidth: 1, lineColor: COLOR.primary }] },
    ],
    margin: [0, 0, 0, 6],
  };
}

function identityTable(rows: { label: string; value: string }[]): any {
  // Two-column grid — pair rows side-by-side so the panel stays compact
  const pairs: any[] = [];
  for (let i = 0; i < rows.length; i += 2) {
    const left = rows[i];
    const right = rows[i + 1] ?? null;
    pairs.push([
      cell(left.label, "identLabel"),
      cell(left.value, "identValue"),
      right ? cell(right.label, "identLabel") : { text: "" },
      right ? cell(right.value, "identValue") : { text: "" },
    ]);
  }
  return {
    table: {
      widths: [80, "*", 80, "*"],
      body: pairs,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft:   () => 0,
      paddingRight:  () => 6,
      paddingTop:    () => 3,
      paddingBottom: () => 3,
    },
  };
}

function inspectionTable(rows: { label: string; value: string; emphasise?: boolean }[]): any {
  return {
    table: {
      widths: [110, "*"],
      body: rows.map(r => [
        { text: r.label.toUpperCase(), style: "identLabel" },
        { text: r.value, style: "identValue", bold: r.emphasise === true },
      ]),
    },
    layout: {
      hLineWidth: (i: number) => (i === 0 ? 0 : 0.5),
      vLineWidth: () => 0,
      hLineColor: () => COLOR.rule,
      paddingLeft:   () => 0,
      paddingRight:  () => 6,
      paddingTop:    () => 4,
      paddingBottom: () => 4,
    },
  };
}

function checksTable(lines: { label: string; result: string; comment: string }[]): any {
  // Three columns: label / Pass-Fail-NA pill / comment
  return {
    table: {
      widths: ["*", 50, "*"],
      headerRows: 1,
      body: [
        [
          { text: "CHECK",   style: "identLabel" },
          { text: "RESULT",  style: "identLabel", alignment: "center" },
          { text: "COMMENT", style: "identLabel" },
        ],
        ...lines.map((l, i) => {
          const styleKey = l.result === "Pass" ? "checkResultPass"
                         : l.result === "Fail" ? "checkResultFail"
                         :                       "checkResultNa";
          const fill = l.result === "Pass" ? COLOR.okWash
                     : l.result === "Fail" ? COLOR.dangerWash
                     :                       COLOR.rowAlt;
          return [
            { text: l.label, style: "checkLabel", margin: [0, 4, 0, 4] },
            { text: l.result, style: styleKey, fillColor: fill, margin: [0, 4, 0, 4] },
            { text: l.comment || "—", style: "para", color: l.comment ? COLOR.text : COLOR.dim, margin: [0, 4, 0, 4] },
          ];
        }),
      ],
    },
    layout: {
      hLineWidth: (i: number) => (i === 0 ? 1 : 0.5),
      vLineWidth: () => 0,
      hLineColor: (i: number) => (i === 0 ? COLOR.ruleDark : COLOR.rule),
    },
  };
}

function buildDefectBlock(severity: string, summary: string, action: string | null): any {
  return {
    margin: [0, 14, 0, 0],
    table: {
      widths: ["*"],
      body: [[{
        stack: [
          { text: `DEFECT — ${severity.toUpperCase()}`, color: COLOR.danger, bold: true, fontSize: 10, characterSpacing: 1 },
          { text: summary, style: "para", margin: [0, 4, 0, 0] },
          ...(action ? [{ text: `Action required: ${action}`, fontSize: 9, color: COLOR.muted, italics: true, margin: [0, 4, 0, 0] }] : []),
        ],
        fillColor: COLOR.dangerWash,
        margin: [10, 8, 10, 8],
      }]],
    },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
  };
}

function cell(text: string, style: string): any {
  return { text, style };
}

function formatNow(): string {
  return new Date().toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// -----------------------------------------------------------------------------
// Parse the structured "Visual checks:" block out of condition_notes.
// Form composes lines like:
//   - Webbing intact: Pass
//   - Stitching intact: Fail — broken thread, ~5mm
// We split into structured rows for the PDF table; remaining text becomes
// the free-form "Notes" section.
// -----------------------------------------------------------------------------

function splitConditionNotes(text: string, _args: InspectionPdfArgs): {
  checkLines: { label: string; result: string; comment: string }[];
  freeNotes:  string;
} {
  if (!text.trim()) return { checkLines: [], freeNotes: "" };

  const lines = text.split(/\r?\n/);
  const checkLines: { label: string; result: string; comment: string }[] = [];
  const otherLines: string[] = [];

  let inChecksBlock = false;
  let inNotesBlock = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inNotesBlock) otherLines.push("");
      continue;
    }
    if (/^visual checks:?\s*$/i.test(line)) {
      inChecksBlock = true;
      inNotesBlock  = false;
      continue;
    }
    if (/^notes:?\s*$/i.test(line)) {
      inChecksBlock = false;
      inNotesBlock  = true;
      continue;
    }
    if (inChecksBlock && line.startsWith("-")) {
      const body = line.replace(/^-\s*/, "");
      // Match "label: result" or "label: result — comment"
      const m = body.match(/^(.*?):\s*(Pass|Fail|N\/A|NA)\s*(?:[—-]\s*(.+))?$/i);
      if (m) {
        checkLines.push({
          label:   m[1].trim(),
          result:  m[2].toUpperCase() === "NA" ? "N/A" : (m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase()),
          comment: m[3]?.trim() ?? "",
        });
        continue;
      }
    }
    // Anything else falls into freeNotes
    otherLines.push(raw);
  }

  return {
    checkLines,
    freeNotes: otherLines.join("\n").trim(),
  };
}
